import { api } from './api';

export interface SyncScheduleConfig {
  autoSyncEnabled: boolean;
  syncTimeOfDay: string; // HH:MM:SS format
  syncFrequency: 'daily' | 'every_2_days' | 'weekly';
  changeNotificationsEnabled: boolean;
  syncPriority: number; // 0=low, 1=normal, 2=high
}

export interface SyncResult {
  success: boolean;
  matchesFound: number;
  matchesCreated: number;
  matchesUpdated: number;
  matchesDeleted: number;
  duration: number;
  error?: string;
}

export const syncSchedulerService = {
  async getActiveJobs(): Promise<Record<string, string>> {
    const response = await api.get('/leagues/sync/jobs');
    return response.data;
  },

  /**
   * Update sync configuration for a league
   */
  async updateLeagueSyncConfig(
    leagueId: string, 
    config: Partial<SyncScheduleConfig> & { timezone?: string }
  ): Promise<void> {
    await api.put(`/leagues/${leagueId}/sync/schedule`, config);
  },

  /**
   * Manually trigger sync for a league (async)
   */
  async triggerManualSync(leagueId: string): Promise<{ jobId: string }> {
    const response = await api.post<{ success: boolean; data: { jobId: string }; message: string }>(
      `/leagues/${leagueId}/sync`
    );
    return response.data.data;
  },

  /**
   * Pause automatic syncing for a league
   */
  async pauseLeagueSync(leagueId: string): Promise<void> {
    await api.post(`/leagues/${leagueId}/sync/schedule/pause`);
  },

  /**
   * Resume automatic syncing for a league
   */
  async resumeLeagueSync(leagueId: string): Promise<void> {
    await api.post(`/leagues/${leagueId}/sync/schedule/resume`);
  },

  /**
   * Format sync time for display
   */
  formatSyncTime(syncTime: string): string {
    try {
      const [hours, minutes] = syncTime.split(':');
      const hour = parseInt(hours, 10);
      const minute = parseInt(minutes, 10);
      
      const date = new Date();
      date.setHours(hour, minute, 0, 0);
      
      return date.toLocaleTimeString([], { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return syncTime;
    }
  },

  /**
   * Format sync frequency for display
   */
  formatSyncFrequency(frequency: string): string {
    switch (frequency) {
      case 'daily':
        return 'Daily';
      case 'every_2_days':
        return 'Every 2 days';
      case 'weekly':
        return 'Weekly';
      case 'custom':
        return 'Custom';
      default:
        return frequency;
    }
  },

  /**
   * Format sync duration for display
   */
  formatSyncDuration(durationMs: number): string {
    if (durationMs < 1000) {
      return `${durationMs}ms`;
    } else if (durationMs < 60000) {
      return `${(durationMs / 1000).toFixed(1)}s`;
    } else {
      return `${(durationMs / 60000).toFixed(1)}m`;
    }
  },

  /**
   * Format relative time for display
   */
  formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d ago`;
    
    return date.toLocaleDateString();
  },

  /**
   * Get sync priority label
   */
  getSyncPriorityLabel(priority: number): string {
    switch (priority) {
      case 0:
        return 'Low';
      case 1:
        return 'Normal';
      case 2:
        return 'High';
      default:
        return 'Normal';
    }
  },

  /**
   * Get sync priority color
   */
  getSyncPriorityColor(priority: number): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (priority) {
      case 0:
        return 'default';
      case 1:
        return 'primary';
      case 2:
        return 'warning';
      default:
        return 'primary';
    }
  },

  /**
   * Get sync status color
   */
  getSyncStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'in_progress':
        return 'info';
      case 'never':
        return 'default';
      default:
        return 'default';
    }
  },

  /**
   * Get sync time preview for a new league
   */
  async getSyncTimePreview(timezone?: string): Promise<{
    nextSyncAt: string;
    displayTime: string;
    timezone: string;
  }> {
    const params = timezone ? { timezone } : {};
    const response = await api.get('/leagues/sync/schedule/preview', { params });
    return response.data.data;
  }
};
